import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, XCircle, Eye, Trash2, Lock, AlertTriangle, MapPin, Edit, Heart, MessageSquare, Gem, Package, Search } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mineral status helpers
const MINERAL_STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'pending_approval', label: 'Pending' },
  { value: 'under_review', label: 'Review' },
  { value: 'negotiating', label: 'Negotiating' },
  { value: 'contracted', label: 'Contracted' },
  { value: 'rejected', label: 'Rejected' },
];

export function AdminPage() {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [allDeals, setAllDeals] = useState([]);
  const [pendingMinerals, setPendingMinerals] = useState([]);
  const [allMinerals, setAllMinerals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '', type: 'deal' });
  const [editDialog, setEditDialog] = useState({ open: false, deal: null });
  const [editMineralDialog, setEditMineralDialog] = useState({ open: false, mineral: null });
  const [interests, setInterests] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [passcode, setPasscode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [pendingRes, allRes, mineralsRes, allMineralsRes, interestsRes, pendingUsersRes] = await Promise.all([
        axios.get(`${API}/admin/pending-deals`, { headers: getAuthHeaders() }),
        axios.get(`${API}/deals`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/pending-minerals`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/all-minerals`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/interests`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/pending-users`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      setDeals(pendingRes.data);
      setAllDeals(allRes.data);
      setPendingMinerals(mineralsRes.data);
      setAllMinerals(allMineralsRes.data);
      setInterests(interestsRes.data);
      setPendingUsers(pendingUsersRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query || query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/admin/search?q=${encodeURIComponent(query)}`, { headers: getAuthHeaders() });
      setSearchResults(res.data);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  };

  const handleInterestAction = async (interestId, action) => {
    try {
      await axios.post(`${API}/admin/interests/${interestId}/action`, { action }, { headers: getAuthHeaders() });
      const labels = { accept: 'Accepted', decline: 'Declined', add_to_spv: 'Added to SPV', notify_owner: 'Owner Notified' };
      toast.success(labels[action] || 'Action completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to process action');
    }
  };

  const handleApprove = async (dealId, status) => {
    try {
      await axios.post(`${API}/deals/${dealId}/approve`, { status }, { headers: getAuthHeaders() });
      toast.success(`Deal ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleMineralApprove = async (mineralId, status) => {
    try {
      await axios.post(`${API}/minerals/${mineralId}/approve`, { status }, { headers: getAuthHeaders() });
      toast.success(`Mineral ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleDelete = async () => {
    try {
      const endpoint = deleteDialog.type === 'mineral'
        ? `${API}/minerals/${deleteDialog.id}?passcode=${passcode}`
        : `${API}/deals/${deleteDialog.id}?passcode=${passcode}`;
      await axios.delete(endpoint, { headers: getAuthHeaders() });
      toast.success(`${deleteDialog.type === 'mineral' ? 'Mineral' : 'Deal'} deleted`);
      setDeleteDialog({ open: false, id: null, name: '', type: 'deal' });
      setPasscode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleModify = async (dealId, updates) => {
    try {
      await axios.patch(`${API}/deals/${dealId}`, updates, { headers: getAuthHeaders() });
      toast.success('Deal updated');
      fetchData();
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const handleMineralModify = async (mineralId, updates) => {
    try {
      await axios.patch(`${API}/minerals/${mineralId}`, updates, { headers: getAuthHeaders() });
      toast.success('Mineral updated');
      fetchData();
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const handleUserAction = async (userId, action) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/approve`, { action }, { headers: getAuthHeaders() });
      toast.success(`User ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchData();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'internal') {
    return <div className="p-6 text-center text-muted-foreground">Admin access required</div>;
  }

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading...</div></div>;

  return (
    <div className="p-6 space-y-6" data-testid="admin-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Admin Panel</h1>
        <p className="text-muted-foreground">Review submissions, manage listings, and control access</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Pending Deals</p><p className="text-2xl font-bold font-mono text-yellow-600">{deals.length}</p></div><AlertTriangle className="h-8 w-8 text-yellow-500" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Pending Minerals</p><p className="text-2xl font-bold font-mono text-yellow-600">{pendingMinerals.length}</p></div><AlertTriangle className="h-8 w-8 text-yellow-500" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Total Deals</p><p className="text-2xl font-bold font-mono">{allDeals.length}</p></div><Package className="h-8 w-8 text-muted-foreground" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Total Minerals</p><p className="text-2xl font-bold font-mono">{allMinerals.length}</p></div><Gem className="h-8 w-8 text-muted-foreground" /></div></CardContent></Card>
      </div>

      {/* Unified Search Bar */}
      <Card>
        <CardContent className="py-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search deals and minerals by name, country, sector..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
              data-testid="admin-search-input"
            />
          </div>
          {searchQuery.length >= 2 && (
            <div className="mt-3 space-y-1">
              {searching ? (
                <p className="text-xs text-muted-foreground py-2">Searching...</p>
              ) : searchResults.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No results found for "{searchQuery}"</p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-2">{searchResults.length} result(s)</p>
                  {searchResults.map(r => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between p-2 rounded-sm hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate(r.type === 'mineral' ? `/minerals/${r.id}` : `/deals/${r.id}`)}
                      data-testid={`search-result-${r.id}`}
                    >
                      <div className="flex items-center gap-2">
                        {r.type === 'mineral' ? <Gem size={14} className="text-muted-foreground" /> : <Package size={14} className="text-muted-foreground" />}
                        <span className="text-sm font-medium">{r.name}</span>
                        <Badge variant="outline" className="text-[9px] capitalize">{r.type}</Badge>
                        {r.listing_type === 'demo' && <Badge variant="outline" className="text-[9px] border-orange-300 text-orange-600">Demo</Badge>}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{r.country}</span>
                        <Badge variant="outline" className="text-[9px] capitalize">{r.status?.replace(/_/g, ' ')}</Badge>
                        <span>by {r.submitted_by}</span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="pending">
        <TabsList className="flex-wrap">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending Deals ({deals.length})</TabsTrigger>
          <TabsTrigger value="pending-minerals" data-testid="tab-pending-minerals">Pending Minerals ({pendingMinerals.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All Deals ({allDeals.length})</TabsTrigger>
          <TabsTrigger value="all-minerals" data-testid="tab-all-minerals">All Minerals ({allMinerals.length})</TabsTrigger>
          <TabsTrigger value="interests" data-testid="tab-interests">Interests ({interests.length})</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Pending Users ({pendingUsers.length})</TabsTrigger>
        </TabsList>

        {/* Pending Deals */}
        <TabsContent value="pending" className="space-y-3 mt-4">
          {deals.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No pending submissions</CardContent></Card>
          ) : deals.map(deal => (
            <Card key={deal.id} data-testid={`pending-${deal.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 cursor-pointer" onClick={() => navigate(`/deals/${deal.id}`)}>
                    <h3 className="font-semibold text-sm">{deal.name}</h3>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span className="flex items-center gap-1"><MapPin size={10} />{deal.country}</span>
                      <span>{deal.sector}</span>
                      <span className="font-mono">{formatCurrency(deal.capital_required)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleApprove(deal.id, 'approved')} data-testid={`approve-deal-${deal.id}`}>
                      <CheckCircle size={14} className="mr-1" />Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleApprove(deal.id, 'under_review')} data-testid={`review-deal-${deal.id}`}>
                      <Eye size={14} className="mr-1" />Review
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => handleApprove(deal.id, 'rejected')} data-testid={`reject-deal-${deal.id}`}>
                      <XCircle size={14} className="mr-1" />Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Pending Minerals */}
        <TabsContent value="pending-minerals" className="space-y-3 mt-4">
          {pendingMinerals.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No pending mineral submissions</CardContent></Card>
          ) : pendingMinerals.map(mineral => (
            <Card key={mineral.id} data-testid={`pending-mineral-${mineral.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 cursor-pointer" onClick={() => navigate(`/minerals/${mineral.id}`)}>
                    <h3 className="font-semibold text-sm">{mineral.commodity} - {mineral.origin_country}</h3>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span>{mineral.grade}</span>
                      <span className="font-mono">{mineral.volume?.toLocaleString()} {mineral.volume_unit}</span>
                      <span>{mineral.delivery_basis}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleMineralApprove(mineral.id, 'available')} data-testid={`approve-mineral-${mineral.id}`}>
                      <CheckCircle size={14} className="mr-1" />Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleMineralApprove(mineral.id, 'under_review')} data-testid={`review-mineral-${mineral.id}`}>
                      <Eye size={14} className="mr-1" />Review
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => handleMineralApprove(mineral.id, 'rejected')} data-testid={`reject-mineral-${mineral.id}`}>
                      <XCircle size={14} className="mr-1" />Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* All Deals */}
        <TabsContent value="all" className="space-y-3 mt-4">
          {allDeals.map(deal => (
            <Card key={deal.id} data-testid={`manage-${deal.id}`}>
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1 cursor-pointer" onClick={() => navigate(`/deals/${deal.id}`)}>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-sm">{deal.name}</h3>
                      {deal.verification_status === 'verified' && <Badge className="bg-green-100 text-green-700 text-[9px]">Verified</Badge>}
                      {deal.listing_type === 'demo' && <Badge variant="outline" className="text-[9px] border-orange-300 text-orange-600">Demo</Badge>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span>{deal.country}</span>
                      <span>{deal.sector}</span>
                      <span className="font-mono">{formatCurrency(deal.capital_required)}</span>
                      <Badge variant="outline" className="text-[9px] capitalize">{deal.status?.replace(/_/g, ' ')}</Badge>
                      {deal.views > 0 && <span className="flex items-center gap-0.5"><Eye size={10} />{deal.views}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditDialog({ open: true, deal: { ...deal } })} data-testid={`edit-${deal.id}`}>
                      <Edit size={14} className="mr-1" />Edit
                    </Button>
                    <Select value={deal.status} onValueChange={(v) => handleModify(deal.id, { status: v })}>
                      <SelectTrigger className="h-8 w-28 text-xs" data-testid={`status-${deal.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="pending_approval">Pending</SelectItem>
                        <SelectItem value="under_review">Review</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={() => handleModify(deal.id, { verification_status: deal.verification_status === 'verified' ? 'not_verified' : 'verified' })} data-testid={`verify-${deal.id}`}>
                      {deal.verification_status === 'verified' ? 'Unverify' : 'Verify'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleModify(deal.id, { listing_type: deal.listing_type === 'demo' ? 'real' : 'demo' })} data-testid={`toggle-demo-${deal.id}`} className={deal.listing_type === 'demo' ? 'border-orange-300 text-orange-600' : ''}>
                      {deal.listing_type === 'demo' ? 'Set Real' : 'Set Demo'}
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => setDeleteDialog({ open: true, id: deal.id, name: deal.name, type: 'deal' })} data-testid={`delete-btn-${deal.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* All Minerals - NEW */}
        <TabsContent value="all-minerals" className="space-y-3 mt-4">
          {allMinerals.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No mineral listings</CardContent></Card>
          ) : allMinerals.map(mineral => (
            <Card key={mineral.id} data-testid={`manage-mineral-${mineral.id}`}>
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1 cursor-pointer" onClick={() => navigate(`/minerals/${mineral.id}`)}>
                    <div className="flex items-center gap-2">
                      <Gem size={14} className="text-muted-foreground flex-shrink-0" />
                      <h3 className="font-medium text-sm">{mineral.commodity}</h3>
                      {mineral.verification_status === 'verified' && <Badge className="bg-green-100 text-green-700 text-[9px]">Verified</Badge>}
                      {mineral.listing_type === 'demo' && <Badge variant="outline" className="text-[9px] border-orange-300 text-orange-600">Demo</Badge>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span className="flex items-center gap-1"><MapPin size={10} />{mineral.origin_country}</span>
                      <span>{mineral.grade}</span>
                      <span className="font-mono">{mineral.volume?.toLocaleString()} {mineral.volume_unit}</span>
                      <Badge variant="outline" className="text-[9px] capitalize">{mineral.status?.replace(/_/g, ' ')}</Badge>
                      {mineral.views > 0 && <span className="flex items-center gap-0.5"><Eye size={10} />{mineral.views}</span>}
                      {mineral.ai_score && <span className="font-mono text-[10px]">AI: {mineral.ai_score}</span>}
                      <span className="text-[10px]">by {mineral.seller_name}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditMineralDialog({ open: true, mineral: { ...mineral } })} data-testid={`edit-mineral-${mineral.id}`}>
                      <Edit size={14} className="mr-1" />Edit
                    </Button>
                    <Select value={mineral.status} onValueChange={(v) => handleMineralModify(mineral.id, { status: v })}>
                      <SelectTrigger className="h-8 w-28 text-xs" data-testid={`mineral-status-${mineral.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MINERAL_STATUS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={() => handleMineralModify(mineral.id, { verification_status: mineral.verification_status === 'verified' ? 'not_verified' : 'verified' })} data-testid={`verify-mineral-${mineral.id}`}>
                      {mineral.verification_status === 'verified' ? 'Unverify' : 'Verify'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleMineralModify(mineral.id, { listing_type: mineral.listing_type === 'demo' ? 'real' : 'demo' })} data-testid={`toggle-demo-mineral-${mineral.id}`} className={mineral.listing_type === 'demo' ? 'border-orange-300 text-orange-600' : ''}>
                      {mineral.listing_type === 'demo' ? 'Set Real' : 'Set Demo'}
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => setDeleteDialog({ open: true, id: mineral.id, name: mineral.commodity, type: 'mineral' })} data-testid={`delete-mineral-btn-${mineral.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Interests */}
        <TabsContent value="interests" className="space-y-3 mt-4">
          {interests.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No interest requests yet</CardContent></Card>
          ) : interests.map(interest => (
            <Card key={interest.id} data-testid={`interest-${interest.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded flex items-center justify-center ${interest.type === 'express_interest' ? 'bg-pink-100' : 'bg-purple-100'}`}>
                      {interest.type === 'express_interest' ? <Heart size={14} className="text-pink-600" /> : <MessageSquare size={14} className="text-purple-600" />}
                    </div>
                    <div>
                      <h3 className="font-medium text-sm">{interest.type === 'express_interest' ? 'Interest Expressed' : 'Info Requested'}</h3>
                      <p className="text-xs text-muted-foreground">{interest.user_name} ({interest.user_role}) &middot; {interest.entity_name}</p>
                      {interest.message && <p className="text-xs text-muted-foreground mt-1 italic">{interest.message}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {interest.status === 'new' ? (
                      <>
                        <Button size="sm" variant="default" className="text-xs h-7" onClick={() => handleInterestAction(interest.id, 'accept')} data-testid={`accept-${interest.id}`}>Accept</Button>
                        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleInterestAction(interest.id, 'decline')} data-testid={`decline-${interest.id}`}>Decline</Button>
                        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleInterestAction(interest.id, 'add_to_spv')} data-testid={`spv-${interest.id}`}>Add to SPV</Button>
                        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleInterestAction(interest.id, 'notify_owner')} data-testid={`notify-${interest.id}`}>Notify Owner</Button>
                      </>
                    ) : (
                      <Badge variant="outline" className={`text-[10px] capitalize ${interest.status === 'accept' ? 'border-green-400 text-green-600' : interest.status === 'decline' ? 'border-red-400 text-red-600' : 'border-blue-400 text-blue-600'}`}>
                        {interest.status === 'add_to_spv' ? 'SPV Added' : interest.status === 'notify_owner' ? 'Owner Notified' : interest.status}
                      </Badge>
                    )}
                    <Badge variant="outline" className={`text-[9px] capitalize ${interest.entity_type === 'mineral' ? 'border-teal-400 text-teal-600' : ''}`}>{interest.entity_type}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Pending Users */}
        <TabsContent value="users" className="space-y-3 mt-4">
          {pendingUsers.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No pending user registrations</CardContent></Card>
          ) : pendingUsers.map(u => (
            <Card key={u.id} data-testid={`pending-user-${u.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm">{u.full_name}</h3>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span>{u.email}</span>
                      <span>{u.organization}</span>
                      <Badge variant="outline" className="text-[9px] capitalize">{u.role?.replace(/_/g, ' ')}</Badge>
                      <span>{u.country}</span>
                      {u.email_verified
                        ? <Badge className="bg-green-100 text-green-700 text-[9px]">Email Verified</Badge>
                        : <Badge variant="outline" className="text-[9px] border-yellow-400 text-yellow-600">Email Not Verified</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleUserAction(u.id, 'approve')} data-testid={`approve-user-${u.id}`}>
                      <CheckCircle size={14} className="mr-1" />Approve
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => handleUserAction(u.id, 'reject')} data-testid={`reject-user-${u.id}`}>
                      <XCircle size={14} className="mr-1" />Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Edit Deal Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit size={18} />Edit Deal</DialogTitle></DialogHeader>
          {editDialog.deal && (
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={editDialog.deal.name || ''} onChange={(e) => setEditDialog({ ...editDialog, deal: { ...editDialog.deal, name: e.target.value } })} data-testid="edit-name" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sector</Label>
                  <Select value={editDialog.deal.sector || ''} onValueChange={(v) => setEditDialog({ ...editDialog, deal: { ...editDialog.deal, sector: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Energy / Oil & Gas">Energy / Oil & Gas</SelectItem>
                      <SelectItem value="Minerals">Minerals</SelectItem>
                      <SelectItem value="Other Industrial">Other Industrial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Stage</Label>
                  <Select value={editDialog.deal.stage || ''} onValueChange={(v) => setEditDialog({ ...editDialog, deal: { ...editDialog.deal, stage: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="concept">Concept</SelectItem>
                      <SelectItem value="feasibility">Feasibility</SelectItem>
                      <SelectItem value="development">Development</SelectItem>
                      <SelectItem value="financial_close">Financial Close</SelectItem>
                      <SelectItem value="construction">Construction</SelectItem>
                      <SelectItem value="operational">Operational</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Capital Required (USD)</Label><Input type="number" value={editDialog.deal.capital_required || ''} onChange={(e) => setEditDialog({ ...editDialog, deal: { ...editDialog.deal, capital_required: Number(e.target.value) } })} data-testid="edit-capital" /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditDialog({ open: false, deal: null })}>Cancel</Button>
                <Button onClick={async () => {
                  await handleModify(editDialog.deal.id, {
                    name: editDialog.deal.name,
                    sector: editDialog.deal.sector,
                    stage: editDialog.deal.stage,
                    capital_required: editDialog.deal.capital_required
                  });
                  setEditDialog({ open: false, deal: null });
                }} data-testid="save-edit-btn">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Mineral Dialog */}
      <Dialog open={editMineralDialog.open} onOpenChange={(open) => setEditMineralDialog({ ...editMineralDialog, open })}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit size={18} />Edit Mineral Listing</DialogTitle></DialogHeader>
          {editMineralDialog.mineral && (() => {
            const m = editMineralDialog.mineral;
            const setField = (field, value) => setEditMineralDialog({ ...editMineralDialog, mineral: { ...m, [field]: value } });
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Commodity</Label><Input value={m.commodity || ''} onChange={(e) => setField('commodity', e.target.value)} data-testid="edit-mineral-commodity" /></div>
                  <div><Label>Grade / Specification</Label><Input value={m.grade || ''} onChange={(e) => setField('grade', e.target.value)} data-testid="edit-mineral-grade" /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Volume</Label><Input type="number" value={m.volume || ''} onChange={(e) => setField('volume', Number(e.target.value))} data-testid="edit-mineral-volume" /></div>
                  <div><Label>Volume Unit</Label><Input value={m.volume_unit || ''} onChange={(e) => setField('volume_unit', e.target.value)} /></div>
                  <div><Label>Origin Country</Label><Input value={m.origin_country || ''} onChange={(e) => setField('origin_country', e.target.value)} data-testid="edit-mineral-country" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Delivery Basis</Label><Input value={m.delivery_basis || ''} onChange={(e) => setField('delivery_basis', e.target.value)} /></div>
                  <div><Label>Pricing Basis</Label><Input value={m.pricing_basis || ''} onChange={(e) => setField('pricing_basis', e.target.value)} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Contract Duration</Label><Input value={m.contract_duration || ''} onChange={(e) => setField('contract_duration', e.target.value)} /></div>
                  <div><Label>Target Buyers</Label><Input value={m.target_buyers || ''} onChange={(e) => setField('target_buyers', e.target.value)} /></div>
                </div>
                <div><Label>Description</Label><Input value={m.description || ''} onChange={(e) => setField('description', e.target.value)} data-testid="edit-mineral-description" /></div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setEditMineralDialog({ open: false, mineral: null })}>Cancel</Button>
                  <Button onClick={async () => {
                    await handleMineralModify(m.id, {
                      commodity: m.commodity, grade: m.grade, volume: m.volume,
                      volume_unit: m.volume_unit, origin_country: m.origin_country,
                      delivery_basis: m.delivery_basis, pricing_basis: m.pricing_basis,
                      contract_duration: m.contract_duration, target_buyers: m.target_buyers,
                      description: m.description
                    });
                    setEditMineralDialog({ open: false, mineral: null });
                  }} data-testid="save-mineral-edit-btn">Save Changes</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog (shared for deals and minerals) */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => { setDeleteDialog({ ...deleteDialog, open }); setPasscode(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock size={18} />Confirm Deletion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">You are about to permanently delete this {deleteDialog.type}:</p>
            <p className="font-semibold">{deleteDialog.name}</p>
            <div>
              <Label>Enter Admin Passcode</Label>
              <Input type="password" value={passcode} onChange={(e) => setPasscode(e.target.value)} placeholder="Enter passcode to confirm" data-testid="delete-passcode" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, id: null, name: '', type: 'deal' })}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={!passcode} data-testid="confirm-delete-btn">Delete {deleteDialog.type === 'mineral' ? 'Mineral' : 'Deal'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
